/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import React from 'react';
import PropTypes from 'prop-types';
import { Row as Row$1, Column } from '@carbon/react';
import LayoutWrapper from '../../../Utilities/layout/LayoutWrapper.js';
import { emptyArray } from '../../../Utilities/propHelpers/defaultProps.js';
import classnames from 'classnames';

/* eslint-disable react/no-array-index-key */
var Header = function Header(_ref) {
  var children = _ref.children;
  return /*#__PURE__*/React.createElement(Row$1, null, /*#__PURE__*/React.createElement(Column, null, children));
};
Header.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element])
};
var Row = function Row(_ref2) {
  var className = _ref2.className,
    hasEqualCols = _ref2.hasEqualCols,
    hasMotion = _ref2.hasMotion,
    _ref2$firstChildIndex = _ref2.firstChildIndex,
    firstChildIndex = _ref2$firstChildIndex === void 0 ? 0 : _ref2$firstChildIndex,
    children = _ref2.children;
  return /*#__PURE__*/React.createElement(Row$1, {
    as: "section",
    className: classnames('cpc--grid-layout__row', className)
  }, hasEqualCols ? React.Children.map(children, function (child, i) {
    return /*#__PURE__*/React.createElement(Column
    // eslint-disable-next-line react/no-array-index-key
    , {
      key: i,
      className: classnames("cpc--motion-child-".concat(firstChildIndex + i), {
        'cpc--grid-layout--motion': hasMotion
      })
    }, child);
  }) : React.Children.map(children, function (child, i) {
    if (!child) return null;
    return /*#__PURE__*/React.cloneElement(child, {
      key: i,
      className: classnames(child.props.className, "cpc--motion-child-".concat(firstChildIndex + i), {
        'cpc--grid-layout--motion': hasMotion
      })
    });
  }));
};
Row.propTypes = {
  className: PropTypes.string,
  hasEqualCols: PropTypes.bool,
  hasMotion: PropTypes.bool,
  firstChildIndex: PropTypes.number,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element])
};

/*
 * LayoutWrapper provides Carbon's grid and generic Layout styles
 */

var GridLayout = function GridLayout(_ref3) {
  var className = _ref3.className,
    _ref3$gridMode = _ref3.gridMode,
    gridMode = _ref3$gridMode === void 0 ? 'wide' : _ref3$gridMode,
    _ref3$children = _ref3.children,
    children = _ref3$children === void 0 ? emptyArray : _ref3$children,
    hasMotion = _ref3.hasMotion;
  // childIndex counts the number of children within Rows to properly apply delay
  var childIndex = 0;
  var gridProps = {
    condensed: gridMode === 'condensed',
    narrow: gridMode === 'narrow',
    fullWidth: gridMode === 'wide'
  };
  return /*#__PURE__*/React.createElement(LayoutWrapper, {
    hasMotion: hasMotion,
    className: "cpc--grid-layout ".concat(className),
    gridProps: gridProps
  }, React.Children.map(children, function (child) {
    var firstChildIndex = childIndex;
    childIndex += React.Children.count(child);
    return /*#__PURE__*/React.cloneElement(child, {
      hasMotion: hasMotion,
      firstChildIndex: firstChildIndex
    });
  }));
};
GridLayout.propTypes = {
  className: PropTypes.string,
  gridMode: PropTypes.oneOf(['wide', 'condensed', 'narrow']),
  hasMotion: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element])
};
GridLayout.Header = Header;
GridLayout.Row = Row;
var GridLayout$1 = GridLayout;

export { GridLayout$1 as default };
